import React from 'react';
import { cn } from '../../lib/utils';

// Animated Button with ripple effect
export function AnimatedButton({ 
  children, 
  className = '', 
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  ...props 
}) {
  const [ripples, setRipples] = React.useState([]);

  const handleClick = (e) => {
    if (disabled || loading) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const newRipple = { x, y, id: Date.now() };
    setRipples([...ripples, newRipple]);
    
    setTimeout(() => {
      setRipples(ripples => ripples.filter(r => r.id !== newRipple.id));
    }, 600);
    
    onClick?.(e);
  };

  const variants = {
    primary: 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30',
    secondary: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30',
    success: 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg shadow-green-500/25 hover:shadow-xl hover:shadow-green-500/30',
    warning: 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30',
    outline: 'bg-transparent border-2 border-blue-500 text-blue-500 hover:bg-blue-50',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-100',
  };

  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl',
  };

  return (
    <button
      className={cn(
        'relative overflow-hidden rounded-xl font-semibold transition-all duration-300',
        'transform hover:-translate-y-0.5 active:translate-y-0',
        'focus:outline-none focus:ring-4 focus:ring-blue-500/20',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none',
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled || loading}
      onClick={handleClick}
      {...props}
    >
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="absolute bg-white/30 rounded-full animate-ripple pointer-events-none"
          style={{
            left: ripple.x,
            top: ripple.y,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}
      
      <span className={cn('relative flex items-center justify-center gap-2', loading && 'opacity-0')}>
        {children}
      </span>
      
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </span>
      )}
    </button>
  );
}

// Hover Card with 3D effect
export function HoverCard3D({ children, className = '' }) {
  const [transform, setTransform] = React.useState({ rotateX: 0, rotateY: 0 });
  const cardRef = React.useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = ((y - centerY) / centerY) * -10;
    const rotateY = ((x - centerX) / centerX) * 10;
    
    setTransform({ rotateX, rotateY });
  };

  const handleMouseLeave = () => {
    setTransform({ rotateX: 0, rotateY: 0 });
  };

  return (
    <div
      ref={cardRef}
      className={cn(
        'transition-transform duration-200 ease-out',
        className
      )}
      style={{
        transform: `perspective(1000px) rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg)`,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>
  );
}

// Magnetic Button (follows cursor)
export function MagneticButton({ children, className = '', strength = 0.3 }) {
  const buttonRef = React.useRef(null);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!buttonRef.current) return;
    
    const rect = buttonRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    setPosition({ x: x * strength, y: y * strength });
  };

  const handleMouseLeave = () => {
    setPosition({ x: 0, y: 0 });
  };

  return (
    <button
      ref={buttonRef}
      className={cn('transition-transform duration-200', className)}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </button>
  );
}

// Glow Card
export function GlowCard({ children, className = '', glowColor = 'blue' }) {
  const colors = {
    blue: 'hover:shadow-[0_0_30px_rgba(0,82,255,0.3)]',
    purple: 'hover:shadow-[0_0_30px_rgba(124,58,237,0.3)]',
    green: 'hover:shadow-[0_0_30px_rgba(16,185,129,0.3)]',
    cyan: 'hover:shadow-[0_0_30px_rgba(0,212,255,0.3)]',
    pink: 'hover:shadow-[0_0_30px_rgba(236,72,153,0.3)]',
  };

  return (
    <div
      className={cn(
        'transition-all duration-300 rounded-2xl',
        colors[glowColor],
        className
      )}
    >
      {children}
    </div>
  );
}

// Floating Label Input
export function FloatingInput({ 
  label, 
  type = 'text', 
  value, 
  onChange, 
  className = '',
  error = '',
  ...props 
}) {
  const [focused, setFocused] = React.useState(false);
  const hasValue = value && value.length > 0;

  return (
    <div className={cn('relative', className)}>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className={cn(
          'w-full h-14 px-4 pt-5 pb-2 rounded-xl border-2 transition-all duration-200',
          'focus:outline-none peer',
          error 
            ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/20' 
            : 'border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20'
        )}
        placeholder=" "
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        {...props}
      />
      <label
        className={cn(
          'absolute left-4 transition-all duration-200 pointer-events-none',
          'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base',
          'peer-focus:top-2 peer-focus:text-xs peer-focus:text-blue-500',
          (hasValue || focused) ? 'top-2 text-xs text-blue-500' : 'top-1/2 -translate-y-1/2 text-gray-400'
        )}
      >
        {label}
      </label>
      {error && (
        <p className="mt-1 text-sm text-red-500 animate-shake">{error}</p>
      )}
    </div>
  );
}

// Animated Counter
export function AnimatedCounter({ value, duration = 1000, prefix = '', suffix = '' }) {
  const [displayValue, setDisplayValue] = React.useState(0);
  
  React.useEffect(() => {
    const startTime = Date.now();
    const startValue = displayValue;
    const endValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    
    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      setDisplayValue(Math.floor(startValue + (endValue - startValue) * easeOut));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [value, duration]);

  return (
    <span>
      {prefix}{displayValue.toLocaleString()}{suffix}
    </span>
  );
}

// Progress Bar with animation
export function AnimatedProgress({ value, max = 100, color = 'blue', showLabel = true }) {
  const percentage = Math.min((value / max) * 100, 100);
  
  const colors = {
    blue: 'from-blue-500 to-cyan-500',
    purple: 'from-purple-500 to-pink-500',
    green: 'from-green-500 to-emerald-500',
    orange: 'from-orange-500 to-amber-500',
  };

  return (
    <div className="w-full">
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full bg-gradient-to-r rounded-full transition-all duration-1000 ease-out',
            colors[color]
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showLabel && (
        <div className="flex justify-between mt-1 text-sm text-gray-500">
          <span>{value}</span>
          <span>{max}</span>
        </div>
      )}
    </div>
  );
}

// Notification Badge with pulse
export function NotificationBadge({ count, className = '' }) {
  if (!count || count <= 0) return null;
  
  return (
    <span
      className={cn(
        'absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center',
        'bg-red-500 text-white text-xs font-bold rounded-full px-1.5',
        'animate-pulse',
        className
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

// Tooltip with animation
export function AnimatedTooltip({ children, content, position = 'top' }) {
  const [visible, setVisible] = React.useState(false);
  
  const positions = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      <div
        className={cn(
          'absolute z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg whitespace-nowrap',
          'transition-all duration-200 pointer-events-none',
          positions[position],
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        )}
      >
        {content}
        <div className={cn(
          'absolute w-2 h-2 bg-gray-900 rotate-45',
          position === 'top' && 'top-full left-1/2 -translate-x-1/2 -mt-1',
          position === 'bottom' && 'bottom-full left-1/2 -translate-x-1/2 -mb-1',
          position === 'left' && 'left-full top-1/2 -translate-y-1/2 -ml-1',
          position === 'right' && 'right-full top-1/2 -translate-y-1/2 -mr-1',
        )} />
      </div>
    </div>
  );
}

// Success Checkmark Animation
export function SuccessCheckmark({ size = 64 }) {
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox="0 0 52 52" className="w-full h-full">
        <circle
          cx="26"
          cy="26"
          r="24"
          fill="none"
          stroke="url(#gradient)"
          strokeWidth="3"
          className="animate-circle-draw"
          style={{ strokeDasharray: 166, strokeDashoffset: 166 }}
        />
        <path
          fill="none"
          stroke="url(#gradient)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M14 27l7 7 16-16"
          className="animate-check-draw"
          style={{ strokeDasharray: 48, strokeDashoffset: 48, animationDelay: '0.3s' }}
        />
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#10B981" />
            <stop offset="100%" stopColor="#059669" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

export default {
  AnimatedButton,
  HoverCard3D,
  MagneticButton,
  GlowCard,
  FloatingInput,
  AnimatedCounter,
  AnimatedProgress,
  NotificationBadge,
  AnimatedTooltip,
  SuccessCheckmark,
};
